import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Search, LayoutGrid, List, Building2, User, Calendar, AlertTriangle, FileCheck2, FileX2, Plus, Upload } from "lucide-react";
import Layout from "../components/Layout";
import { CargaMasivaDialog } from "../components/propiedad/CargaMasivaDialog";
import { ESTADO_PROPIEDAD, formatCurrency, formatDate } from "../lib/propiedadUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Propiedades({ user, onLogout }) {
  const navigate = useNavigate();
  const [propiedades, setPropiedades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState(() => sessionStorage.getItem("props_vista") || "tarjetas");
  const [search, setSearch] = useState(() => sessionStorage.getItem("props_search") || "");
  const [estadoFilter, setEstadoFilter] = useState(() => sessionStorage.getItem("props_estado") || "todos");
  const [cobranzaFilter, setCobranzaFilter] = useState(() => sessionStorage.getItem("props_cobranza") || "todos");
  const [expedienteFilter, setExpedienteFilter] = useState(() => sessionStorage.getItem("props_expediente") || "todos");
  const [showCargaMasiva, setShowCargaMasiva] = useState(false);
  const esPropietario = user.rol === "propietario";

  useEffect(() => {
    fetchPropiedades();
  }, []);

  useEffect(() => {
    sessionStorage.setItem("props_vista", vista);
    sessionStorage.setItem("props_search", search);
    sessionStorage.setItem("props_estado", estadoFilter);
    sessionStorage.setItem("props_cobranza", cobranzaFilter);
    sessionStorage.setItem("props_expediente", expedienteFilter);
  }, [vista, search, estadoFilter, cobranzaFilter, expedienteFilter]);

  const fetchPropiedades = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/propiedades`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPropiedades(response.data);
    } catch (error) {
      toast.error("Error al cargar las propiedades");
    } finally {
      setLoading(false);
    }
  };

  const filtered = propiedades.filter((p) => {
    const s = search.toLowerCase();
    const matchSearch =
      !s ||
      (p.nombre_corto || "").toLowerCase().includes(s) ||
      (p.direccion || "").toLowerCase().includes(s) ||
      (p.propietario_nombre || "").toLowerCase().includes(s) ||
      (p.inquilino_nombre || "").toLowerCase().includes(s);
    const matchEstado = estadoFilter === "todos" || p.estado === estadoFilter;
    const matchCobranza = cobranzaFilter === "todos" || p.estado_cobranza === cobranzaFilter;
    const matchExpediente =
      expedienteFilter === "todos" ||
      (expedienteFilter === "completo" ? p.expediente_completo : !p.expediente_completo);
    return matchSearch && matchEstado && matchCobranza && matchExpediente;
  });

  const EstadoBadge = ({ estado }) => {
    const cfg = ESTADO_PROPIEDAD[estado] || { label: estado, className: "bg-gray-100 text-gray-700" };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
        {cfg.label}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center py-20 text-gray-500">Cargando propiedades...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="propiedades-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Propiedades</h1>
            <p className="text-sm text-gray-500 mt-1">
              {filtered.length} de {propiedades.length} inmuebles
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!esPropietario && (
              <>
                <Button onClick={() => navigate("/propiedades/nueva")} data-testid="nueva-propiedad-btn">
                  <Plus className="w-4 h-4 mr-1" /> Nueva propiedad
                </Button>
                <Button variant="outline" onClick={() => setShowCargaMasiva(true)} data-testid="carga-masiva-btn">
                  <Upload className="w-4 h-4 mr-1" /> Carga masiva
                </Button>
              </>
            )}
            <Button
              variant={vista === "tarjetas" ? "default" : "outline"}
              size="sm"
              onClick={() => setVista("tarjetas")}
              data-testid="vista-tarjetas-btn"
            >
              <LayoutGrid className="w-4 h-4 mr-1" /> Tarjetas
            </Button>
            <Button
              variant={vista === "tabla" ? "default" : "outline"}
              size="sm"
              onClick={() => setVista("tabla")}
              data-testid="vista-tabla-btn"
            >
              <List className="w-4 h-4 mr-1" /> Tabla
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar inmueble, propietario, inquilino..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="propiedades-search-input"
                />
              </div>
              <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                <SelectTrigger data-testid="filtro-estado-propiedad">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los estados</SelectItem>
                  {Object.entries(ESTADO_PROPIEDAD).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={cobranzaFilter} onValueChange={setCobranzaFilter}>
                <SelectTrigger data-testid="filtro-cobranza-propiedad">
                  <SelectValue placeholder="Cobranza" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Toda la cobranza</SelectItem>
                  <SelectItem value="al_corriente">Al corriente</SelectItem>
                  <SelectItem value="pendiente">Con saldo pendiente</SelectItem>
                  <SelectItem value="vencido">Con saldo vencido</SelectItem>
                </SelectContent>
              </Select>
              <Select value={expedienteFilter} onValueChange={setExpedienteFilter}>
                <SelectTrigger data-testid="filtro-expediente-propiedad">
                  <SelectValue placeholder="Expediente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los expedientes</SelectItem>
                  <SelectItem value="completo">Expediente completo</SelectItem>
                  <SelectItem value="incompleto">Expediente incompleto</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-gray-500">
              <Building2 className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              No se encontraron propiedades con los filtros seleccionados
            </CardContent>
          </Card>
        ) : vista === "tarjetas" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p) => (
              <Card
                key={p.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/propiedades/${p.id}`)}
                data-testid={`propiedad-card-${p.id}`}
              >
                <CardContent className="pt-5 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{p.nombre_corto}</h3>
                      <p className="text-xs text-gray-500 truncate">{p.direccion}</p>
                    </div>
                    <EstadoBadge estado={p.estado} />
                  </div>

                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="truncate">Prop: {p.propietario_nombre || "—"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="truncate">Inq: {p.inquilino_nombre || "—"}</span>
                    </div>
                    {p.fecha_termino_contrato && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span>Contrato hasta {formatDate(p.fecha_termino_contrato)}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-end justify-between pt-2 border-t border-gray-100">
                    <div>
                      <p className="text-xs text-gray-500">Renta mensual</p>
                      <p className="font-semibold text-gray-900">
                        {p.renta_mensual != null ? formatCurrency(p.renta_mensual) : "—"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Saldo pendiente</p>
                      <p className={`font-semibold ${p.saldo_vencido > 0 ? "text-red-600" : p.saldo_pendiente > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                        {formatCurrency(p.saldo_pendiente || 0)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1 text-gray-500">
                      {p.expediente_completo ? (
                        <><FileCheck2 className="w-3.5 h-3.5 text-emerald-500" /> Expediente completo</>
                      ) : (
                        <><FileX2 className="w-3.5 h-3.5 text-amber-500" /> Expediente incompleto</>
                      )}
                    </span>
                    {p.proximo_vencimiento && (
                      <span className="text-gray-500">Próx. venc: {formatDate(p.proximo_vencimiento)}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="propiedades-tabla">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                    <th className="px-4 py-3">Inmueble</th>
                    <th className="px-4 py-3">Propietario</th>
                    <th className="px-4 py-3">Inquilino</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3 text-right">Renta</th>
                    <th className="px-4 py-3 text-right">Saldo pend.</th>
                    <th className="px-4 py-3">Próx. venc.</th>
                    <th className="px-4 py-3">Fin contrato</th>
                    <th className="px-4 py-3 text-center">Expediente</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/propiedades/${p.id}`)}
                      data-testid={`propiedad-row-${p.id}`}
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-indigo-600">{p.nombre_corto}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[200px]">{p.direccion}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{p.propietario_nombre || "—"}</td>
                      <td className="px-4 py-3 text-gray-700">{p.inquilino_nombre || "—"}</td>
                      <td className="px-4 py-3"><EstadoBadge estado={p.estado} /></td>
                      <td className="px-4 py-3 text-right">{p.renta_mensual != null ? formatCurrency(p.renta_mensual) : "—"}</td>
                      <td className={`px-4 py-3 text-right font-medium ${p.saldo_vencido > 0 ? "text-red-600" : p.saldo_pendiente > 0 ? "text-amber-600" : "text-gray-700"}`}>
                        {formatCurrency(p.saldo_pendiente || 0)}
                        {p.saldo_vencido > 0 && <AlertTriangle className="w-3.5 h-3.5 inline ml-1 text-red-500" />}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{p.proximo_vencimiento ? formatDate(p.proximo_vencimiento) : "—"}</td>
                      <td className="px-4 py-3 text-gray-700">{p.fecha_termino_contrato ? formatDate(p.fecha_termino_contrato) : "—"}</td>
                      <td className="px-4 py-3 text-center">
                        {p.expediente_completo ? (
                          <FileCheck2 className="w-4 h-4 text-emerald-500 inline" />
                        ) : (
                          <FileX2 className="w-4 h-4 text-amber-500 inline" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
      <CargaMasivaDialog open={showCargaMasiva} onOpenChange={setShowCargaMasiva} onSuccess={fetchPropiedades} />
    </Layout>
  );
}
